import { Collapsible } from '@ui/animated/Collapsible'
import AnimatedEmpty from '@ui/animated/AnimatedEmpty'
import { useAnimatedOpacity } from '@ui/animated/useAnimatedOpacity'
import { LoadingFooter } from '@ui/lists/LoadingFooter'
import ButtonsFixed from '@ui/buttons/ButtonsFixed'
import { ErpTemple } from '@ca/common'
import EmptyResult from '@ui/components/EmptyResult'
import Page from '@shell/Page'
import Header from '@ui/elements/Header'
import { FlashList } from '@shopify/flash-list'
import TempleItem from '@frame/Frame/Temple'
import { router, useLocalSearchParams } from 'expo-router'
import {
  useSavedTemples,
  useTempleActions,
  useTempleSearch,
} from 'hooks/temple/useTemples'
import ConfigDebugModal from 'features/debug/ConfigDebugModal'
import { useRefreshByUser } from 'hooks/useRefreshByUser'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { View } from 'react-native'
import Animated from 'react-native-reanimated'
import cn from '@ui/utils/cn'
import Search from './Input'

export default function FrameTempleSearch() {
  const { t } = useTranslation(['frame', 'ui'])
  const text = 'frame:temple.search'

  const { q } = useLocalSearchParams<{ q: string }>()

  const search = useTempleSearch({ query: q || '', limit: 10 })
  const { savedIds, isSaved } = useSavedTemples()
  const { saveTemple, remove } = useTempleActions()

  const { isRefetchingByUser, refetchByUser } = useRefreshByUser(
    search.refetch,
    search.isRefetching
  )

  const isLoadings = search.isFetching || search.isLoading
  const showLoading = isLoadings && !!q
  const showEmpty =
    !isLoadings && !!q && !!search.data && search.data.data.length === 0
  const showItems = !!search.data && search.data.data.length > 0

  const styleLoading = useAnimatedOpacity(showLoading)
  const styleEmpty = useAnimatedOpacity(showEmpty)
  const styleItems = useAnimatedOpacity(showItems)

  const handlePress = useCallback(
    async (temple: ErpTemple) => {
      const key = String(temple.id)
      if (savedIds.has(key)) {
        remove.mutate({ id: key })
        return
      }
      await saveTemple(temple)
    },
    [remove, saveTemple, savedIds]
  )

  return (
    <>
      <Page stage="frame" list>
        <FlashList
          ListHeaderComponent={
            <>
              <Header
                list
                title={t(`${text}.header.title`)}
                desc={t(`${text}.header.desc`)}
              />
              <View className="px-8">
                <Search isFetching={isLoadings} />
              </View>
              <Collapsible open={showLoading} fullWidth>
                <LoadingFooter style={styleLoading} />
              </Collapsible>
            </>
          }
          ListFooterComponent={null}
          data={search.data?.data || []}
          extraData={savedIds}
          numColumns={1}
          keyboardShouldPersistTaps="handled"
          contentContainerClassName="pb-8"
          onEndReachedThreshold={0.25}
          onRefresh={refetchByUser}
          refreshing={isRefetchingByUser}
          progressViewOffset={100}
          renderItem={({ item }) => (
            <Animated.View style={styleItems} className={cn('px-8 pt-8')}>
              <TempleItem
                temple={item}
                isSaved={isSaved(item.id)}
                onPress={() => handlePress(item)}
                config={{ isSearchScreen: true }}
              />
            </Animated.View>
          )}
          ListEmptyComponent={
            <AnimatedEmpty style={styleEmpty}>
              <EmptyResult
                title={t(`${text}.empty.result.title`)}
                desc={t(`${text}.empty.result.desc`)}
              />
            </AnimatedEmpty>
          }
        />
      </Page>
      <ConfigDebugModal />
      <ButtonsFixed
        // hidden={savedIds.size === 0}
        buttons={[
          {
            variant: 'outlined',
            label: t(`frame:temple.select.header.title`),
            iconStart: 'arrow-narrow-left',
            onPress: () => {
              router.dismissTo('/frame/temple/select')
            },
          },
        ]}
      />
    </>
  )
}
